import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { authedAtTimestampAtom } from '../../../auth/state';
import { useGetTelehealthSessionInfo } from '../../../hooks';
import { useVideoPlatform } from '../../../hooks/useVideoPlatform';
import { useTrackEvent } from '../../../segment/segment';
import { EventTypeEnum } from '../../../segment/types';
import { normalizeVideoPlatform } from '../../../video-sdk';
import { admissionTrackingHistory } from './analytics/AdmissionTrackingHistory';

function withAdmissionTransactionId<T extends object>(payload: T): T & { txId: string } {
  return {
    ...payload,
    txId: admissionTrackingHistory.getTransactionId() ?? '',
  };
}

export function useAdmissionTrackingEvents() {
  const { userViewed, userClicked } = useTrackEvent();
  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId;
  const videoPlatform = normalizeVideoPlatform(useVideoPlatform());
  const authedAtTimestamp = useAtomValue(authedAtTimestampAtom);

  return useMemo(() => {
    const payload = {
      patientShortId: patientShortId ?? '',
      providerShortId: providerShortId ?? '',
      appointmentShortId: appointmentShortId ?? '',
      source: videoPlatform ?? '',
      isClientUserAuthenticated: !!authedAtTimestamp,
    };
    return {
      trackAdmissionPromptViewed: () => {
        userViewed(EventTypeEnum.ADMISSION_PROMPT_VIEWED, withAdmissionTransactionId(payload));
      },
      trackJoinSessionClicked: () => {
        userClicked(EventTypeEnum.JOIN_SESSION_CLICKED, withAdmissionTransactionId(payload));
      },
      trackAdmissionPromptDismissed: () => {
        userClicked(EventTypeEnum.ADMISSION_PROMPT_DISMISSED, withAdmissionTransactionId(payload));
      },
    };
  }, [patientShortId, providerShortId, appointmentShortId, videoPlatform, userViewed, userClicked, authedAtTimestamp]);
}
